/**
 * Idle sign-out (task 63).
 *
 * Sixty minutes without input ends MDR's session in this tab, through
 * identity-client's `signOut` with reason `idle` and no token (Story 53,
 * design r28). Only MDR's tokens go (bluesphere-identity H7): no
 * `POST /sign-out`, so Ticketing and every other app keep their sessions;
 * the sign-out button ends them. The tab's signed-out marker stops the
 * gate from signing it straight back in. Activity is any pointer, key,
 * scroll or touch event, sampled at most once a second so the listener
 * costs nothing.
 *
 * identity-client then goes to managed login's `/logout`, which ends its
 * one-hour cookie but no app's tokens. After sixty idle minutes that
 * cookie has normally lapsed already (design r28, deviation 29).
 *
 * The clock is wall time, not a timer: a laptop lid closed for two hours
 * fires no events and no timers, so on the next tick (or when the tab
 * becomes visible again) the elapsed time is measured against the last
 * activity stamp and the sign-out happens then. The stamp lives in
 * localStorage so several tabs share one idle clock.
 *
 * This is the idle limit; the absolute limit is the BlueSphere session's
 * refresh-token life (12 hours, bluesphere-identity r31 §SO item 4), after
 * which renewal fails and the gate shows the session's end.
 */

import { signOut as identitySignOut } from '@bluesphere/identity-client';
import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';

import { identityConfig } from './oidc';

export const IDLE_LIMIT_MS = 60 * 60 * 1000;
const STORAGE_KEY = 'bsmdr.lastActivity';
const SAMPLE_MS = 1000;
const CHECK_MS = 30_000;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart'] as const;

function readStamp(): number {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeStamp(now: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    /* storage unavailable: the in-memory sample below still works */
  }
}

export function IdleTimeout({ limitMs = IDLE_LIMIT_MS }: { limitMs?: number }) {
  const auth = useAuth();
  const active = auth.isAuthenticated;

  useEffect(() => {
    if (!active) return;

    let last = Date.now();
    writeStamp(last);
    let lastSample = 0;
    let signingOut = false;

    const touch = () => {
      const now = Date.now();
      if (now - lastSample < SAMPLE_MS) return;
      lastSample = now;
      last = now;
      writeStamp(now);
    };

    const signOut = async () => {
      if (signingOut) return;
      signingOut = true;
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      // H7: no token, so no POST /sign-out; the gate shows the
      // inactivity note next.
      await identitySignOut(() => auth.removeUser(), identityConfig, 'idle');
    };

    const check = () => {
      // Another tab's activity counts: take the newest stamp anywhere.
      const shared = readStamp();
      const newest = Math.max(last, shared);
      if (Date.now() - newest >= limitMs) void signOut();
    };

    for (const name of ACTIVITY_EVENTS) window.addEventListener(name, touch, { passive: true });
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(check, CHECK_MS);

    return () => {
      for (const name of ACTIVITY_EVENTS) window.removeEventListener(name, touch);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [active, auth, limitMs]);

  return null;
}
